import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import useWorkflowRunInstance from "./useWorkflowRunInstance";
import useWorkflowRunStart from "./useWorkflowRunStart";
import useWorkflowRunRefNo from "./useWorkflowRunRefNo";
import useWorkflowDefinitionPreview, { WorkflowRunStartRef } from "./useWorkflowDefinitionPreview";
import { PifStartAnswers } from "../features/workflows/pifCaseValues";

/**
 * Where the Run screen's case comes from, read from the URL: a case to load (?instanceId=), or a
 * workflow to start — a definition version (?definitionId=) or a
 * workflow's latest published version by Token (?token=). Owns the whole real-case lifecycle in
 * front of the stage screens — definition preview, start (with its URL-held idempotency key), the
 * redirect to ?instanceId=, and the load.
 */
const useWorkflowRunSource = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const definitionId = Number(searchParams.get("definitionId"));
  const token = searchParams.get("token")?.trim() || null;
  const instanceId = Number(searchParams.get("instanceId"));
  // A share link (/wf/{RefNo}, decision 6.5) arrives as ?refNo= and is swapped for ?instanceId=.
  const refNo = (!instanceId && searchParams.get("refNo")?.trim()) || null;
  const requestedStageId = searchParams.get("stageId");
  const startKey = searchParams.get("start");
  // Set by the Builder's Preview / Run button. A run opened from the Library instead offers a way
  // back to the Library, matching the prototype's two entry points.
  const fromBuilder = searchParams.get("source") === "builder";
  const fromInstances = searchParams.get("source") === "instances";

  const realInstanceId = instanceId > 0 ? instanceId : null;
  const {
    state: caseLoad,
    retry: retryCaseLoad,
    refresh: refreshCase,
    refreshQuietly: refreshCaseQuietly,
    refreshing: caseRefreshing,
  } = useWorkflowRunInstance(realInstanceId);
  const runCase = caseLoad.status === "loaded" ? caseLoad.runCase : null;

  const { state: refNoLookup, retry: retryRefNoLookup } = useWorkflowRunRefNo(refNo);

  // Once found, the URL names the case, exactly as if it had been opened from Instances.
  useEffect(() => {
    if (refNoLookup.status !== "found") return;
    const next = new URLSearchParams({ instanceId: String(refNoLookup.instanceId) });
    const source = searchParams.get("source");
    if (source) next.set("source", source);
    setSearchParams(next, { replace: true });
  }, [refNoLookup, searchParams, setSearchParams]);

  // Run / Test on a workflow starts a fresh case. The idempotency key rides in the URL so a refresh
  // before the redirect replays the same case on the server rather than creating another. (Epic
  // 80431 retired the built-in demo definitions, whose ids 1-3 and 7 shadowed real ones here.)
  const realStart = useMemo<WorkflowRunStartRef | null>(() => {
    if (instanceId || refNo) return null;
    if (token) return { token };
    return definitionId > 0 ? { definitionId } : null;
  }, [instanceId, refNo, token, definitionId]);

  // The definition is read BEFORE anything is started: the PIF must not create a case until its
  // HR gate is answered "Yes" and an identity resolved (AC2), so a PIF waits on pif-1 with no case
  // behind it; any other definition starts straight away.
  const { state: definitionPreview, retry: retryDefinitionPreview } = useWorkflowDefinitionPreview(realStart);
  const previewDefinition = definitionPreview.status === "loaded" ? definitionPreview.definition : undefined;
  const previewIsPif = Boolean(previewDefinition?.isFlagship);
  // Set by Continue on pif-1: the go-ahead to start the case (a not-yet-started PIF), and the answers
  // the Run screen then saves to it. Kept until they are saved, so a failed save can be retried.
  const [pifStartAnswers, setPifStartAnswers] = useState<PifStartAnswers | null>(null);
  const isPifPreStart = Boolean(realStart && previewIsPif && !pifStartAnswers);

  const definition = runCase?.definition ?? previewDefinition;

  // The case starts on the version that was read (by Token, the latest published one); once it has
  // started, the screen runs on the case's own pinned graph (see loadWorkflowRun).
  const { state: caseStart, retry: retryCaseStart } = useWorkflowRunStart(
    realStart && startKey && previewDefinition && (!previewIsPif || pifStartAnswers)
      ? { definitionId: previewDefinition.id, idempotencyKey: startKey, isTest: fromBuilder }
      : null,
  );

  useEffect(() => {
    if (!realStart || startKey) return;
    const next = new URLSearchParams(searchParams);
    next.set("start", uuidv4());
    setSearchParams(next, { replace: true });
  }, [realStart, startKey, searchParams, setSearchParams]);

  // Once started, the URL names the case — a refresh from here on reloads it rather than
  // starting another.
  useEffect(() => {
    if (caseStart.status !== "started" || instanceId) return;
    const next = new URLSearchParams({ instanceId: String(caseStart.instanceId) });
    const source = searchParams.get("source");
    if (source) next.set("source", source);
    setSearchParams(next, { replace: true });
  }, [caseStart, instanceId, searchParams, setSearchParams]);

  /**
   * Start another case — of the same definition version, or by Token of the latest published one:
   * the URL drops the finished case and its start key, so the preview + start flow above runs
   * again with a fresh idempotency key.
   */
  const startAnother = useCallback(
    (another: WorkflowRunStartRef) => {
      const next = new URLSearchParams(
        "token" in another ? { token: another.token } : { definitionId: String(another.definitionId) },
      );
      const source = searchParams.get("source");
      if (source) next.set("source", source);
      setPifStartAnswers(null);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    startAnother,
    requestedStageId,
    fromBuilder,
    fromInstances,
    realInstanceId,
    /** The share link's RefNo while it resolves; null once the URL names the case. */
    refNo,
    refNoLookup,
    retryRefNoLookup,
    caseLoad,
    retryCaseLoad,
    refreshCase,
    refreshCaseQuietly,
    /** A reload after a write is in flight; the stage on screen is about to be replaced. */
    caseRefreshing,
    runCase,
    realStart,
    definitionPreview,
    retryDefinitionPreview,
    caseStart,
    retryCaseStart,
    pifStartAnswers,
    setPifStartAnswers,
    isPifPreStart,
    definition,
  };
};

export type WorkflowRunSource = ReturnType<typeof useWorkflowRunSource>;

export default useWorkflowRunSource;
